import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { CustomerLoading } from '../../component/loading/loading';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import i18n from '../../i18n';
import { copyToClipboard } from './menu-store.helpers';
import { MenuStoreCommonDialog } from './menu-store-common-dialog.component';

type MenuStoreGenerateLicenseDialogProps = {
    /** 弹窗开关。 */
    open: boolean;
    /** 当前菜单商店文档 id，打开时触发 `onGenerate`。 */
    menuId: string | undefined;
    /** 生成请求中。 */
    loading: boolean;
    /** 接口返回的授权码（可多条）。 */
    generatedCodes: string[];
    /** 调用 `POST .../license-codes` 生成授权码。 */
    onGenerate: (menuId: string) => void;
    /** 关闭弹窗。 */
    onClose: () => void;
};

/**
 * 生成授权码弹窗（打开即请求 Apifox：`POST /v3/menu-store/menu/{id}/license-codes`）。
 */
export function MenuStoreGenerateLicenseDialog(props: MenuStoreGenerateLicenseDialogProps): React.ReactElement | null {
    /** 组件属性。 */
    const { open, menuId, loading, generatedCodes, onGenerate, onClose } = props;
    /** 避免 React 18 StrictMode 下 useEffect 重复触发时重复提交生成请求 */
    const generatedForOpenRef = useRef<string | null>(null);
    /** 稳定引用父组件传入的生成回调，避免因函数引用变化重复请求 */
    const onGenerateRef = useRef(onGenerate);
    onGenerateRef.current = onGenerate;

    useEffect(() => {
        if (!open || !menuId) {
            generatedForOpenRef.current = null;
            return;
        }
        /** 同一次打开仅触发一次生成（默认 count=1 由 state 传入） */
        if (generatedForOpenRef.current === menuId) {
            return;
        }
        generatedForOpenRef.current = menuId;
        onGenerateRef.current(menuId);
    }, [open, menuId]);

    if (!open) {
        return null;
    }
    /** 展示用主授权码（多条时换行拼接后复制） */
    const displayCode = generatedCodes.length > 0 ? generatedCodes.join('\n') : '';
    return (
        <MenuStoreCommonDialog
            title={i18n.t('menu_store_generate_license_title')}
            onClose={onClose}
            dialogClassName="menu-store-generate-dialog"
            hideTitleIcon
            hideBottomClose
            footer={(
                <Box className="menu-store-activate-dialog__footer menu-store-generate-dialog__footer">
                    <LoadingOverlayButton
                        className="menu-store-activate-dialog__submit menu-store-activate-dialog__submit--activate menu-store-dialog__generate-submit"
                        variant="contained"
                        disableElevation
                        fullWidth
                        loading={loading}
                        loadingIndicatorSize={22}
                        disabled={!displayCode}
                        onClick={() => copyToClipboard(displayCode)}
                    >
                        {i18n.t('menu_store_dialog_copy')}
                    </LoadingOverlayButton>
                </Box>
            )}
        >
            <Box className="menu-store-dialog__generate-row">
                <Typography component="span" className="menu-store-dialog__generate-label">
                    {i18n.t('menu_store_license_col_code')}
                </Typography>
                <Box
                    className="menu-store-dialog__generate-code-host"
                    role={loading ? 'status' : undefined}
                    aria-live={loading ? 'polite' : undefined}
                    aria-label={loading ? i18n.t('general_text_loading') : undefined}
                >
                    {loading ? (
                        <CustomerLoading />
                    ) : (
                        <Typography component="span" className="menu-store-dialog__generate-code">
                            {displayCode || '—'}
                        </Typography>
                    )}
                </Box>
            </Box>
        </MenuStoreCommonDialog>
    );
}
